import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../../components/TanTable';
import { Drawer } from '../../../components/ui/Drawer';
import Notification, { NotificationContainer } from '../../../components/Notification';
import complianceAuditService from '../../../services/compliance/complianceAudit.service';
import type {
  ComplianceAuditLogEntry,
  ComplianceAuditLogFilters,
} from '../../../types/compliance';
import { useUserNames } from '../../../hooks/useUserNames';

// Action-family colors (teal/blue/purple/amber/gray).
const ACTION_FAMILY_COLOR: Record<string, string> = {
  third_party: 'bg-teal-200 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
  contract: 'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  contract_document: 'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  vendor: 'bg-purple-200 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  reminder: 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  reminder_schedule: 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
};

const ACTION_PREFIX_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All actions' },
  { value: 'compliance.third_party.', label: 'Third Parties' },
  { value: 'compliance.contract.', label: 'Contracts' },
  { value: 'compliance.contract_document.', label: 'Contract Documents' },
  { value: 'compliance.vendor.', label: 'Vendors' },
  { value: 'compliance.reminder.', label: 'Reminders' },
  { value: 'compliance.reminder_schedule.', label: 'Reminder Schedules' },
];

const ENTITY_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All entities' },
  { value: 'contract', label: 'contract' },
  { value: 'contract_document', label: 'contract_document' },
  { value: 'third_party', label: 'third_party' },
  { value: 'vendor', label: 'vendor' },
  { value: 'reminder_schedule', label: 'reminder_schedule' },
  { value: 'reminder_log', label: 'reminder_log' },
];

// -- Helpers ------------------------------------------------------------------

function actionFamily(action: string): string {
  // "compliance.vendor.approved" -> "vendor"
  // "compliance.reminder_schedule.created" -> "reminder_schedule"
  const parts = action.split('.');
  return parts.length >= 2 ? parts[1] : 'other';
}

function actionShort(action: string): string {
  return action.replace(/^compliance\./, '');
}

function entityLinkFor(entry: ComplianceAuditLogEntry): string | null {
  if (!entry.entity_id) return null;
  switch (entry.entity_type) {
    case 'vendor':
      return `/compliance/tprm/vendors/${entry.entity_id}`;
    case 'reminder_schedule':
    case 'reminder_log':
      return '/compliance/reminders';
    // contract / third_party / contract_document intentionally omitted:
    // those entities use a list + drawer pattern with no standalone detail
    // route, so a /compliance/contracts/:id link falls through to the
    // catch-all and navigates away. Render as plain text until the list
    // pages honor a row-preselect query param.
    default:
      return null;
  }
}

function diffKeys(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
): string[] {
  if (!before && !after) return [];
  const b = before || {};
  const a = after || {};
  const keys = new Set<string>([...Object.keys(b), ...Object.keys(a)]);
  return Array.from(keys).filter((k) => JSON.stringify(b[k]) !== JSON.stringify(a[k]));
}

function summaryFor(entry: ComplianceAuditLogEntry): string {
  if (entry.action.endsWith('.status_changed')) {
    const oldS = (entry.before_data as Record<string, unknown> | null)?.status ?? '?';
    const newS = (entry.after_data as Record<string, unknown> | null)?.status ?? '?';
    return `${String(oldS)} → ${String(newS)}`;
  }
  if (entry.action.endsWith('.created') || entry.action.endsWith('.deleted')) return '';
  const n = diffKeys(entry.before_data, entry.after_data).length;
  return n ? `${n} field${n === 1 ? '' : 's'} changed` : '';
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

function formatTimestamp(iso: string): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return (
      `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ` +
      `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
    );
  } catch {
    return iso;
  }
}

function truncateId(id: string | null, n = 8): string {
  if (!id) return '';
  return id.length > n ? `${id.slice(0, n)}…` : id;
}

// -- JSON panel ---------------------------------------------------------------

function JsonPanel({
  title,
  data,
  changedKeys,
}: {
  title: string;
  data: Record<string, unknown> | null;
  changedKeys: Set<string>;
}) {
  return (
    <div className="flex-1 min-w-0">
      <div className="text-xs font-semibold text-muted uppercase tracking-wide mb-1">
        {title}
      </div>
      {data == null ? (
        <div className="text-sm italic text-muted bg-gray-50 dark:bg-gray-800 rounded p-3 border border-gray-200 dark:border-gray-700">
          (none)
        </div>
      ) : (
        <pre className="text-xs bg-gray-50 dark:bg-gray-800 rounded p-3 border border-gray-200 dark:border-gray-700 overflow-x-auto whitespace-pre-wrap">
          {Object.keys(data).length === 0
            ? <span className="italic text-gray-500">{'{}'}</span>
            : Object.entries(data).map(([k, v]) => {
                const isRedacted = v === '[redacted]';
                const isChanged = changedKeys.has(k);
                return (
                  <div key={k} className={isChanged ? 'font-semibold' : ''}>
                    <span className="text-blue-700 dark:text-blue-300">{JSON.stringify(k)}</span>
                    <span>: </span>
                    <span
                      className={
                        isRedacted
                          ? 'italic text-muted'
                          : 'text-gray-800 dark:text-gray-100'
                      }
                    >
                      {JSON.stringify(v)}
                    </span>
                  </div>
                );
              })}
        </pre>
      )}
    </div>
  );
}

// -- Main component -----------------------------------------------------------

export default function ComplianceAuditLog() {
  const { nameFor } = useUserNames();
  const [searchParams] = useSearchParams();

  // Seed filters from URL query string (enables cross-links from detail pages).
  const initialFilters = useMemo<ComplianceAuditLogFilters>(() => {
    const f: ComplianceAuditLogFilters = {};
    const entityType = searchParams.get('entity_type');
    const entityId = searchParams.get('entity_id');
    const actionPrefix = searchParams.get('action_prefix');
    const actorEmail = searchParams.get('actor_email');
    const dateFrom = searchParams.get('date_from');
    const dateTo = searchParams.get('date_to');
    if (entityType) f.entity_type = entityType;
    if (entityId) f.entity_id = entityId;
    if (actionPrefix) f.action_prefix = actionPrefix;
    if (actorEmail) f.actor_email = actorEmail;
    if (dateFrom) f.date_from = dateFrom;
    if (dateTo) f.date_to = dateTo;
    return f;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only on mount

  const [data, setData] = useState<ComplianceAuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<ComplianceAuditLogFilters>(initialFilters);
  const [actorEmailInput, setActorEmailInput] = useState(initialFilters.actor_email || '');
  const [entityIdInput, setEntityIdInput] = useState(initialFilters.entity_id || '');
  const [selected, setSelected] = useState<ComplianceAuditLogEntry | null>(null);
  const [errorNotification, setErrorNotification] =
    useState<{ title: string; message: string } | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await complianceAuditService.list(filters);
      setData(rows);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } }; message?: string };
      const msg =
        err?.response?.data?.detail || err?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to load audit log', message: msg });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Debounce actor_email substring input.
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = actorEmailInput.trim() || undefined;
      setFilters((f) => (f.actor_email === next ? f : { ...f, actor_email: next }));
    }, 300);
    return () => clearTimeout(timer);
  }, [actorEmailInput]);

  // Debounce entity_id exact input.
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = entityIdInput.trim() || undefined;
      setFilters((f) => (f.entity_id === next ? f : { ...f, entity_id: next }));
    }, 300);
    return () => clearTimeout(timer);
  }, [entityIdInput]);

  const setFilter = <K extends keyof ComplianceAuditLogFilters>(
    key: K,
    value: ComplianceAuditLogFilters[K],
  ) => {
    setFilters((f) => ({ ...f, [key]: (value as unknown) || undefined }));
  };

  const columns = useMemo<ColumnDef<ComplianceAuditLogEntry>[]>(
    () => [
      {
        accessorKey: 'created_at',
        header: () => <span className="pl-3">Timestamp</span>,
        enableSorting: true,
        size: 180,
        cell: ({ row }) => (
          <span className="pl-3 text-xs">
            {formatTimestamp(row.original.created_at)}
          </span>
        ),
      },
      {
        accessorKey: 'action',
        header: 'Action',
        enableSorting: true,
        size: 260,
        cell: ({ row }) => {
          const action = row.original.action;
          const family = actionFamily(action);
          const color =
            ACTION_FAMILY_COLOR[family] ||
            'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
          return (
            <span className={`px-2 py-0.5 text-xs font-medium rounded ${color}`}>
              {actionShort(action)}
            </span>
          );
        },
      },
      {
        accessorKey: 'actor_email',
        header: 'Actor',
        enableSorting: true,
        size: 220,
        cell: ({ row }) => {
          const email = row.original.actor_email;
          const oid = row.original.actor_oid;
          // No person = an automatic action (reminder job, auto-expiry...).
          if (!email && !oid) return <span className="italic text-gray-500">System</span>;
          return (
            <span title={oid || undefined} className="truncate block">
              {email || nameFor(oid) || <span className="italic text-gray-500">{truncateId(oid, 12)}</span>}
            </span>
          );
        },
      },
      {
        accessorKey: 'entity_type',
        header: 'Entity',
        enableSorting: true,
        size: 220,
        cell: ({ row }) => {
          const e = row.original;
          const link = entityLinkFor(e);
          const label = e.entity_id
            ? `${e.entity_type}: ${truncateId(e.entity_id, 8)}`
            : e.entity_type;
          if (link) {
            return (
              <Link
                to={link}
                className="text-blue-600 dark:text-blue-400 hover:underline"
                onClick={(ev) => ev.stopPropagation()}
                title={e.entity_id || undefined}
              >
                {label}
              </Link>
            );
          }
          return (
            <span title={e.entity_id || undefined}>
              {label}
            </span>
          );
        },
      },
      {
        id: 'summary',
        header: 'Summary',
        enableSorting: false,
        size: 240,
        cell: ({ row }) => {
          const s = summaryFor(row.original);
          if (!s) return <span className="text-gray-400">—</span>;
          return <span className="text-sm">{s}</span>;
        },
      },
    ],
    [nameFor],
  );

  const selectCls =
    'px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white';

  const selectedChangedKeys = useMemo(() => {
    if (!selected) return new Set<string>();
    return new Set(diffKeys(selected.before_data, selected.after_data));
  }, [selected]);

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-heading">
            Compliance Audit Log
          </h1>
          <p className="text-sm text-muted mt-1">
            Read-only view of every compliance-entity mutation (most recent first).
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={async () => {
              try {
                await complianceAuditService.exportCsv(filters);
              } catch (error: any) {
                const msg =
                  error?.response?.data?.detail ||
                  error?.message ||
                  'Unknown error';
                setErrorNotification({ title: 'Export failed', message: msg });
              }
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
            title="Export current filter as CSV"
          >
            Export CSV
          </button>
          <button
            onClick={loadData}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            title="Refresh"
          >
            <ArrowPathIcon
              className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Action</label>
          <select
            value={filters.action_prefix || ''}
            onChange={(e) => setFilter('action_prefix', e.target.value || undefined)}
            className={selectCls}
          >
            {ACTION_PREFIX_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Entity Type</label>
          <select
            value={filters.entity_type || ''}
            onChange={(e) => setFilter('entity_type', e.target.value || undefined)}
            className={selectCls}
          >
            {ENTITY_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Actor Email</label>
          <input
            type="text"
            value={actorEmailInput}
            onChange={(e) => setActorEmailInput(e.target.value)}
            placeholder="substring match..."
            className={selectCls}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Entity ID</label>
          <input
            type="text"
            value={entityIdInput}
            onChange={(e) => setEntityIdInput(e.target.value)}
            placeholder="exact id..."
            className={selectCls}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Date From</label>
          <input
            type="date"
            value={filters.date_from || ''}
            onChange={(e) => setFilter('date_from', e.target.value || undefined)}
            className={selectCls}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Date To</label>
          <input
            type="date"
            value={filters.date_to || ''}
            onChange={(e) => setFilter('date_to', e.target.value || undefined)}
            className={selectCls}
          />
        </div>
      </div>

      <TanTable
        data={data}
        columns={columns}
        loading={loading}
        settingsKey="compliance-audit-log-table"
        fieldName="compliance-audit-log-table"
        searchPlaceholder="Search audit events..."
        enableGrouping={false}
        enableColumnFilters={true}
        cardTitle="Audit Events"
        onRowClick={(row) => setSelected(row)}
      />

      <Drawer
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        disableClickOutside
        title={
          selected ? (
            <div className="flex items-center gap-2">
              <span>Audit Event</span>
              <span className="text-xs text-muted">
                {formatTimestamp(selected.created_at)}
              </span>
              <span
                className={`px-2 py-0.5 text-xs font-medium rounded ${
                  ACTION_FAMILY_COLOR[actionFamily(selected.action)] ||
                  'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}
              >
                {actionShort(selected.action)}
              </span>
            </div>
          ) : (
            'Audit Event'
          )
        }
        maxWidth="xl"
        width="56rem"
      >
        {selected && (
          <div className="space-y-5 text-sm">
            {/* Metadata */}
            <section>
              <div className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">
                Metadata
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                <div>
                  <dt className="text-xs text-muted">Event ID</dt>
                  <dd className="text-xs break-all">{selected.id}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted">Timestamp</dt>
                  <dd className="text-xs">{formatTimestamp(selected.created_at)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted">Actor</dt>
                  <dd>
                    {selected.actor_oid
                      ? nameFor(selected.actor_oid)
                      : selected.actor_email || <span className="italic text-gray-500">System</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted">Actor Email</dt>
                  <dd>{selected.actor_email || <span className="italic text-gray-500">(none)</span>}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted">Actor OID</dt>
                  <dd className="text-xs break-all">
                    {selected.actor_oid || <span className="italic text-gray-500">(none)</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted">Entity Type</dt>
                  <dd>{selected.entity_type}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted">Entity ID</dt>
                  <dd className="text-xs break-all">
                    {selected.entity_id || <span className="italic text-gray-500">(none)</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted">IP Address</dt>
                  <dd className="text-xs">
                    {selected.ip_address || <span className="italic text-gray-500">(none)</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted">User Agent</dt>
                  <dd className="text-xs break-all">
                    {selected.user_agent || <span className="italic text-gray-500">(none)</span>}
                  </dd>
                </div>
              </dl>
            </section>

            {/* Changed fields */}
            {selectedChangedKeys.size > 0 && (
              <section>
                <div className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">
                  Changed Fields ({selectedChangedKeys.size})
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {Array.from(selectedChangedKeys).map((k) => (
                    <span
                      key={k}
                      className="px-2 py-0.5 text-xs rounded bg-flag text-flag-fg"
                    >
                      {k}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* Before / After */}
            <section>
              <div className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">
                Data Diff
              </div>
              <div className="flex flex-col md:flex-row gap-4">
                <JsonPanel
                  title="Before"
                  data={selected.before_data}
                  changedKeys={selectedChangedKeys}
                />
                <JsonPanel
                  title="After"
                  data={selected.after_data}
                  changedKeys={selectedChangedKeys}
                />
              </div>
            </section>

            {/* Event metadata */}
            {selected.event_metadata && Object.keys(selected.event_metadata).length > 0 && (
              <section>
                <div className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">
                  Event Metadata
                </div>
                <pre className="text-xs bg-gray-50 dark:bg-gray-800 rounded p-3 border border-gray-200 dark:border-gray-700 overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(selected.event_metadata, null, 2)}
                </pre>
              </section>
            )}
          </div>
        )}
      </Drawer>

      {errorNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>
      )}
    </div>
  );
}
